/**
 * Campos TABLA del catalogo, en formato movil.
 *
 * En EMSYS web una TABLA se pinta como grilla: una fila por colaborador y una
 * columna por dato. En un telefono eso no funciona — cinco columnas no entran y
 * escribir dentro de celdas de 60px es pelearse con el teclado. Aca se usa el
 * patron de lista + hoja: cada fila es una tarjeta que se lee de un vistazo, y
 * se carga o se edita en una hoja modal donde cada dato ocupa el ancho
 * completo.
 *
 * Lo que NO cambia es el contrato: se envia el mismo array de filas que manda
 * la web, con las mismas columnas, porque las columnas salen del catalogo y no
 * de este archivo. Agregar una columna en EMSYS aparece aca sin tocar nada.
 */
import {
  IonButton,
  IonButtons,
  IonChip,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonNote,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  addOutline,
  businessOutline,
  createOutline,
  personOutline,
  trashOutline,
} from "ionicons/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  CampoCatalogo,
  Colaborador,
  ColumnaCatalogo,
  FilaGrilla,
  UnidadNegocio,
  buscarColaboradores,
  columnasDeCarga,
} from "src/services/solicitudesRRHH";

interface Props {
  campo: CampoCatalogo;
  filas: FilaGrilla[];
  unidades: UnidadNegocio[];
  onCambio: (filas: FilaGrilla[]) => void;
}

/** 350000 -> "350.000". Igual que el formato de EMSYS. */
const formatoMiles = (valor: string): string => {
  const crudo = (valor || "").trim();
  // Misma regla que EMSYS: un punto con exactamente tres cifras detras es
  // separador de miles ("350.000"); con una o dos, es decimal ("350.5",
  // "350000.00"). Sin esto, un importe guardado como "350000.00" se leia como
  // 35.000.000, y si alguien abria la fila a editar se llevaba ese numero.
  const esDecimalConPunto = /^-?\d+\.\d{1,2}$/.test(crudo);
  const limpio = esDecimalConPunto
    ? crudo
    : crudo.replace(/\./g, "").replace(",", ".");
  const numero = Number(limpio);
  if (!limpio || Number.isNaN(numero)) return valor || "";
  return numero.toLocaleString("es-PY", { maximumFractionDigits: 2 });
};

/**
 * Las etiquetas visibles de una fila. Se guardan junto al id porque la tarjeta
 * tiene que poder mostrar el nombre del colaborador sin volver a consultar, y
 * EMSYS ignora estas claves (valida solo las columnas del catalogo).
 */
const CLAVE_ETIQUETA = (codigo: string) => `__etiqueta_${codigo}`;

const SolicitudRRHHGrilla: React.FC<Props> = ({
  campo,
  filas,
  unidades,
  onCambio,
}) => {
  // Solo lo que carga el solicitante: el Nº de Factura lo pone Finanzas y el
  // ID de Descuento, RRHH, cada uno desde su bandeja en EMSYS.
  const columnas = columnasDeCarga(campo);
  const tope = campo.max_filas || 0;
  const lleno = tope > 0 && filas.length >= tope;

  const [abierta, setAbierta] = useState(false);
  // null = alta; numero = se esta editando esa fila.
  const [editando, setEditando] = useState<number | null>(null);
  const [borrador, setBorrador] = useState<FilaGrilla>({});
  const [errorHoja, setErrorHoja] = useState("");

  // Busqueda de colaborador dentro de la hoja.
  const [termino, setTermino] = useState("");
  const [resultados, setResultados] = useState<Colaborador[]>([]);
  const [buscando, setBuscando] = useState(false);

  const columnaOperario = useMemo(
    () => columnas.find((c) => c.tipo === "OPERARIO"),
    [columnas],
  );
  const columnasDetalle = useMemo(
    () => columnas.filter((c) => c.tipo !== "OPERARIO"),
    [columnas],
  );

  const total = useMemo(() => {
    const columnaImporte = columnas.find((c) => c.tipo === "DECIMAL");
    if (!columnaImporte) return null;
    const suma = filas.reduce((acumulado, fila) => {
      const crudo = (fila[columnaImporte.codigo] || "")
        .replace(/\./g, "")
        .replace(",", ".");
      const numero = Number(crudo);
      return acumulado + (Number.isNaN(numero) ? 0 : numero);
    }, 0);
    return suma;
  }, [filas, columnas]);

  // Busqueda con debounce: sin esto cada tecla dispara un request.
  useEffect(() => {
    if (!abierta || !columnaOperario) return;
    const texto = termino.trim();
    if (texto.length < 2) {
      setResultados([]);
      return;
    }
    let vigente = true;
    setBuscando(true);
    const timer = window.setTimeout(() => {
      buscarColaboradores(texto)
        .then((filas2) => {
          if (vigente) setResultados(filas2);
        })
        .catch(() => {
          if (vigente) setResultados([]);
        })
        .finally(() => {
          if (vigente) setBuscando(false);
        });
    }, 350);
    return () => {
      vigente = false;
      window.clearTimeout(timer);
    };
  }, [termino, abierta, columnaOperario]);

  const abrirAlta = () => {
    setEditando(null);
    setBorrador({});
    setTermino("");
    setResultados([]);
    setErrorHoja("");
    setAbierta(true);
  };

  const abrirEdicion = (indice: number) => {
    setEditando(indice);
    setBorrador({ ...filas[indice] });
    setTermino("");
    setResultados([]);
    setErrorHoja("");
    setAbierta(true);
  };

  const quitar = (indice: number) => {
    onCambio(filas.filter((_, i) => i !== indice));
  };

  const setCelda = (codigo: string, valor: string, etiqueta?: string) => {
    setBorrador((previo) => {
      const siguiente = { ...previo, [codigo]: valor };
      if (etiqueta !== undefined) {
        siguiente[CLAVE_ETIQUETA(codigo)] = etiqueta;
      }
      return siguiente;
    });
  };

  const elegirColaborador = (colaborador: Colaborador) => {
    if (!columnaOperario) return;
    setCelda(
      columnaOperario.codigo,
      String(colaborador.operario_id),
      colaborador.texto || colaborador.nombre,
    );
    setTermino("");
    setResultados([]);
  };

  /** Valida solo lo obligatorio; EMSYS revalida todo igual. */
  const confirmar = () => {
    const faltante = columnas.find(
      (c) => c.requerido && !String(borrador[c.codigo] || "").trim(),
    );
    if (faltante) {
      setErrorHoja(`${faltante.etiqueta} es obligatorio`);
      return;
    }
    if (editando === null) {
      onCambio([...filas, borrador]);
    } else {
      onCambio(filas.map((fila, i) => (i === editando ? borrador : fila)));
    }
    setAbierta(false);
  };

  const etiquetaDe = (fila: FilaGrilla, columna: ColumnaCatalogo): string => {
    const guardada = fila[CLAVE_ETIQUETA(columna.codigo)];
    if (guardada) return guardada;
    const crudo = fila[columna.codigo] || "";
    if (columna.tipo === "OPCION") {
      const opcion = columna.opciones.find((o) => o.valor === crudo);
      return opcion ? opcion.texto : crudo;
    }
    if (columna.tipo === "UNIDAD_NEGOCIO") {
      const unidad = unidades.find((u) => String(u.id) === crudo);
      return unidad ? unidad.nombre : crudo;
    }
    if (columna.tipo === "DECIMAL") return formatoMiles(crudo);
    return crudo;
  };

  const controlDeCelda = (columna: ColumnaCatalogo) => {
    const valor = borrador[columna.codigo] || "";

    if (columna.tipo === "OPCION") {
      return (
        <IonSelect
          value={valor}
          placeholder="Elegir"
          interface="action-sheet"
          onIonChange={(e) => setCelda(columna.codigo, e.detail.value || "")}
        >
          {columna.opciones.map((o) => (
            <IonSelectOption key={o.valor} value={o.valor}>
              {o.texto}
            </IonSelectOption>
          ))}
        </IonSelect>
      );
    }

    if (columna.tipo === "UNIDAD_NEGOCIO") {
      return (
        <IonSelect
          value={valor}
          placeholder="Elegir"
          interface="action-sheet"
          onIonChange={(e) => setCelda(columna.codigo, e.detail.value || "")}
        >
          {unidades.map((u) => (
            <IonSelectOption key={u.id} value={String(u.id)}>
              {u.nombre}
            </IonSelectOption>
          ))}
        </IonSelect>
      );
    }

    if (columna.tipo === "DECIMAL" || columna.tipo === "ENTERO") {
      return (
        <IonInput
          type="text"
          inputmode="numeric"
          value={valor}
          placeholder={columna.tipo === "DECIMAL" ? "Ej: 350.000" : ""}
          onIonChange={(e) => setCelda(columna.codigo, e.detail.value || "")}
        />
      );
    }

    if (columna.tipo === "FECHA") {
      return (
        <IonInput
          type="date"
          value={valor}
          onIonChange={(e) => setCelda(columna.codigo, e.detail.value || "")}
        />
      );
    }

    return (
      <IonInput
        type="text"
        value={valor}
        onIonChange={(e) => setCelda(columna.codigo, e.detail.value || "")}
      />
    );
  };

  const tituloDe = (fila: FilaGrilla): string => {
    if (!columnaOperario) return `Fila ${filas.indexOf(fila) + 1}`;
    return etiquetaDe(fila, columnaOperario) || "(sin colaborador)";
  };

  const cerrar = useCallback(() => setAbierta(false), []);

  return (
    <>
      {filas.length === 0 && (
        <IonNote
          color="medium"
          className="ion-padding-start"
          style={{ display: "block", fontSize: "0.85rem" }}
        >
          Todavía no agregaste colaboradores.
        </IonNote>
      )}

      <IonList lines="full" style={{ paddingTop: 0 }}>
        {filas.map((fila, indice) => (
          <IonItem key={indice} lines="full">
            <IonIcon icon={personOutline} slot="start" color="medium" />
            <IonLabel className="ion-text-wrap">
              <h3 style={{ fontWeight: 600 }}>{tituloDe(fila)}</h3>
              {columnasDetalle.map((columna) => {
                const texto = etiquetaDe(fila, columna);
                if (!texto) return null;
                return (
                  <p key={columna.codigo} style={{ fontSize: "0.8rem" }}>
                    <IonNote color="medium">{columna.etiqueta}: </IonNote>
                    {texto}
                  </p>
                );
              })}
            </IonLabel>
            <IonButtons slot="end">
              <IonButton
                fill="clear"
                color="medium"
                onClick={() => abrirEdicion(indice)}
              >
                <IonIcon icon={createOutline} slot="icon-only" />
              </IonButton>
              <IonButton fill="clear" color="danger" onClick={() => quitar(indice)}>
                <IonIcon icon={trashOutline} slot="icon-only" />
              </IonButton>
            </IonButtons>
          </IonItem>
        ))}
      </IonList>

      <div
        className="ion-padding-start ion-padding-end"
        style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
      >
        <IonButton
          size="small"
          fill="outline"
          disabled={lleno}
          onClick={abrirAlta}
        >
          <IonIcon icon={addOutline} slot="start" />
          Agregar colaborador
        </IonButton>
        {tope > 0 && (
          <IonChip outline color={lleno ? "warning" : "medium"}>
            {filas.length} de {tope}
          </IonChip>
        )}
        {total !== null && filas.length > 0 && (
          <IonChip outline color="primary">
            Total: {total.toLocaleString("es-PY", { maximumFractionDigits: 2 })}
          </IonChip>
        )}
      </div>

      {/* Modal a pantalla completa: esta version de Ionic no soporta hojas
          con breakpoints. */}
      <IonModal isOpen={abierta} onDidDismiss={cerrar}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {editando === null ? "Agregar colaborador" : "Editar colaborador"}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={cerrar}>Cancelar</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {columnaOperario && (
            <>
              <IonItem lines="none">
                <IonLabel position="stacked">
                  {columnaOperario.etiqueta}
                  {columnaOperario.requerido ? " *" : ""}
                </IonLabel>
              </IonItem>
              {borrador[columnaOperario.codigo] ? (
                <IonItem lines="full">
                  <IonIcon icon={personOutline} slot="start" color="primary" />
                  <IonLabel className="ion-text-wrap">
                    {etiquetaDe(borrador, columnaOperario)}
                  </IonLabel>
                  <IonButton
                    fill="clear"
                    size="small"
                    slot="end"
                    onClick={() => setCelda(columnaOperario.codigo, "", "")}
                  >
                    Cambiar
                  </IonButton>
                </IonItem>
              ) : (
                <>
                  <IonSearchbar
                    value={termino}
                    debounce={0}
                    placeholder="Legajo, cédula o nombre"
                    onIonChange={(e) => setTermino(e.detail.value || "")}
                  />
                  {termino.trim().length > 0 && termino.trim().length < 2 && (
                    <IonNote color="medium" className="ion-padding-start">
                      Escribí al menos 2 caracteres
                    </IonNote>
                  )}
                  {buscando && (
                    <IonNote color="medium" className="ion-padding-start">
                      Buscando...
                    </IonNote>
                  )}
                  <IonList lines="full">
                    {resultados.map((c) => (
                      <IonItem
                        key={c.operario_id}
                        button
                        detail={false}
                        onClick={() => elegirColaborador(c)}
                      >
                        <IonLabel className="ion-text-wrap">
                          <h3>{c.nombre}</h3>
                          <p>
                            Legajo {c.legajo} · CI {c.cedula}
                          </p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </>
              )}
            </>
          )}

          {columnasDetalle.map((columna) => (
            <IonItem key={columna.codigo} lines="full">
              <IonLabel position="stacked">
                {columna.etiqueta}
                {columna.requerido ? " *" : ""}
              </IonLabel>
              {controlDeCelda(columna)}
              {columna.ayuda && (
                <IonNote slot="helper" color="medium">
                  {columna.ayuda}
                </IonNote>
              )}
            </IonItem>
          ))}

          {errorHoja && (
            <IonItem lines="none">
              <IonNote color="danger">{errorHoja}</IonNote>
            </IonItem>
          )}

          <div className="ion-padding">
            <IonButton expand="block" onClick={confirmar}>
              <IonIcon icon={businessOutline} slot="start" />
              {editando === null ? "Agregar a la solicitud" : "Guardar cambios"}
            </IonButton>
          </div>
        </IonContent>
      </IonModal>
    </>
  );
};

export default SolicitudRRHHGrilla;
